import React from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, RefreshCw, ExternalLink, User, MapPin, CalendarDays, FileText, Receipt, AlertTriangle, ClipboardList, Download } from 'lucide-react'
import { useGalleryEnrichmentDetails } from '../../hooks/useGalleryEnrichmentDetails'
import { useGalleryJobLinkage } from '../../hooks/useGalleryJobLinkage'
import { useEnrichGallery } from '../../hooks/useEnrichGallery'
import { getEnrichmentDisplayState, formatEnrichmentDate } from '../../utils/enrichment'
import type { GalleryJobberDetails } from '@/types/gallery'
import { formatCurrency, formatPhoneNumber } from '@/features/integrations/utils/integrationUtils'

interface GalleryEnrichmentPanelProps {
  galleryId?: number | null
  jobNumber?: number | null
  franchise?: string | null
  jobberProfileUrl?: string | null
}

type LineItem = {
  id?: string
  name?: string
  description?: string
  quantity?: number
  // Handle both camelCase and snake_case from different API sources
  unitCost?: number
  unit_cost?: number
  unitPrice?: number
  unit_price?: number
  total?: number
}

const toNumber = (value: unknown) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const parseStructuredData = (value: unknown) => {
  if (!value) return null
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return null
    }
  }
  return value
}

const normalizeLineItems = (value: unknown): LineItem[] => {
  const parsed = parseStructuredData(value)
  if (!parsed) return []
  if (Array.isArray(parsed)) return parsed as LineItem[]
  if (typeof parsed === 'object') {
    if (Array.isArray((parsed as any).items)) return (parsed as any).items as LineItem[]
    if (Array.isArray((parsed as any).lineItems)) return (parsed as any).lineItems as LineItem[]
  }
  return []
}

const normalizeInvoiceDetails = (value: unknown) => {
  const parsed = parseStructuredData(value)
  if (!parsed || typeof parsed !== 'object') {
    return { total: undefined, invoices: [] as any[] }
  }
  const total = toNumber((parsed as any).total)
  const invoicesRaw = (parsed as any).invoices
  const invoices = Array.isArray(invoicesRaw) ? invoicesRaw : []
  return { total, invoices }
}

const getAddress = (details?: GalleryJobberDetails | null) => {
  if (!details) return null
  const segments = [
    details.service_address,
    details.service_city,
    details.service_province,
    details.service_postal_code,
  ].filter(Boolean)
  return segments.join(', ')
}

const formatDateTime = (value?: string | null) => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return parsed.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export default function GalleryEnrichmentPanel({
  galleryId,
  jobNumber,
  franchise,
  jobberProfileUrl,
}: GalleryEnrichmentPanelProps) {
  const { data, isLoading, isFetching, error, refetch } = useGalleryEnrichmentDetails(galleryId)
  const { data: linkage } = useGalleryJobLinkage(galleryId)
  const { mutate: enrich, isPending: isEnriching } = useEnrichGallery(galleryId ?? 0)

  const state = getEnrichmentDisplayState(data)
  const lineItems = React.useMemo(() => normalizeLineItems(data?.line_items), [data?.line_items])
  const invoiceDetails = React.useMemo(() => normalizeInvoiceDetails(data?.invoice_details), [data?.invoice_details])
  const jobberSyncTime = formatDateTime(data?.fetched_on)
  const serviceDate = formatDateTime(data?.service_date)
  const hasData = !!data

  // Check if we can enrich (have job linkage data)
  const canEnrich = !!(linkage?.job_id && linkage?.franchise_region)

  const handleEnrich = () => {
    if (!linkage?.job_id || !linkage?.franchise_region) return
    enrich({
      jobId: linkage.job_id,
      franchise: linkage.franchise_region,
    })
  }

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
          <div>
            <CardTitle>Gallery Enrichment</CardTitle>
            <CardDescription>Outputs captured by the Jobber enrichment agent.</CardDescription>
          </div>
          <div className='space-y-2 text-right text-xs text-muted-foreground'>
            <span className={`inline-flex justify-end`}>
              <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-sm font-medium ${state.pillClass}`}>
                {state.label}
              </span>
            </span>
            {state.description && <div>{state.description}</div>}
            {jobberSyncTime && <div>Last sync {jobberSyncTime}</div>}
          </div>
        </div>
        <div className='flex flex-wrap gap-2'>
          {/* Primary action: Fetch fresh data from Jobber API */}
          {canEnrich && (
            <Button
              size='sm'
              onClick={handleEnrich}
              disabled={isEnriching || !galleryId}
              className='bg-amber-600 hover:bg-amber-700 text-white shadow-sm'
            >
              {isEnriching ? (
                <Loader2 className='mr-2 h-4 w-4 animate-spin' />
              ) : (
                <Download className='mr-2 h-4 w-4' />
              )}
              {isEnriching ? 'Enriching...' : 'Enrich from Jobber'}
            </Button>
          )}
          {/* Secondary action: Re-read cached data from database */}
          <Button variant='outline' size='sm' onClick={() => refetch()} disabled={isFetching || isLoading || !galleryId}>
            {isFetching ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : <RefreshCw className='mr-2 h-4 w-4' />}
            Refresh
          </Button>
          {jobberProfileUrl && (
            <Button size='sm' variant='secondary' asChild>
              <a href={jobberProfileUrl} target='_blank' rel='noopener noreferrer'>
                Open Jobber
                <ExternalLink className='ml-2 h-4 w-4' />
              </a>
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className='space-y-6'>
        {error && (
          <Alert variant='destructive'>
            <AlertDescription>
              Failed to load enrichment details: {error.message}
            </AlertDescription>
          </Alert>
        )}

        {isLoading && (
          <div className='flex items-center justify-center py-10 text-muted-foreground'>
            <Loader2 className='mr-2 h-4 w-4 animate-spin' />
            Loading enrichment data...
          </div>
        )}

        {!isLoading && !hasData && (
          <div className='rounded-lg border border-dashed p-6 text-sm text-muted-foreground space-y-3'>
            <p>No enrichment data yet for this gallery.</p>
            {canEnrich ? (
              <div className='space-y-2'>
                <p>Click the button below to fetch job details from Jobber.</p>
                <Button
                  size='sm'
                  onClick={handleEnrich}
                  disabled={isEnriching}
                  className='bg-amber-600 hover:bg-amber-700 text-white shadow-sm'
                >
                  {isEnriching ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <Download className='mr-2 h-4 w-4' />
                  )}
                  {isEnriching ? 'Enriching...' : 'Enrich from Jobber'}
                </Button>
              </div>
            ) : (
              <p>This gallery needs job images linked before enrichment is available.</p>
            )}
            <div className='text-xs'>Job #{jobNumber ?? '—'} {franchise ? `• ${franchise}` : ''}</div>
          </div>
        )}

        {!isLoading && hasData && (
          <>
            {data?.error_message && (
              <Alert variant='destructive' className='bg-destructive/10'>
                <AlertTriangle className='h-4 w-4' />
                <AlertDescription className='text-sm'>
                  Agent reported an error during the last run: {data.error_message}
                </AlertDescription>
              </Alert>
            )}

            <div className='grid gap-4 md:grid-cols-2'>
              <div className='rounded-lg border p-4 space-y-2'>
                <div className='flex items-center gap-2 text-sm font-semibold'>
                  <User className='h-4 w-4 text-muted-foreground' />
                  Client
                </div>
                <div className='text-base font-medium'>{data?.client_name || 'Unknown client'}</div>
                {data?.client_company && <div className='text-sm text-muted-foreground'>{data.client_company}</div>}
                <div className='text-xs text-muted-foreground space-y-1'>
                  {data?.client_email && <div>{data.client_email}</div>}
                  {data?.client_phone && <div>{formatPhoneNumber(data.client_phone)}</div>}
                </div>
              </div>
              <div className='rounded-lg border p-4 space-y-2'>
                <div className='flex items-center gap-2 text-sm font-semibold'>
                  <MapPin className='h-4 w-4 text-muted-foreground' />
                  Service Location
                </div>
                <div className='text-base font-medium'>{getAddress(data) || 'Address unavailable'}</div>
                {serviceDate && (
                  <div className='flex items-center gap-2 text-xs text-muted-foreground'>
                    <CalendarDays className='h-4 w-4' />
                    {serviceDate}
                  </div>
                )}
                {jobNumber && (
                  <div className='text-xs text-muted-foreground'>Job #{jobNumber}</div>
                )}
                {data?.job_id && (
                  <div className='text-xs text-muted-foreground'>Jobber ID: {data.job_id}</div>
                )}
              </div>
            </div>

            <div className='grid gap-4 lg:grid-cols-2'>
              <div className='rounded-lg border p-4 space-y-2'>
                <div className='flex items-center gap-2 text-sm font-semibold'>
                  <ClipboardList className='h-4 w-4 text-muted-foreground' />
                  Line Items
                </div>
                {lineItems.length === 0 ? (
                  <p className='text-sm text-muted-foreground'>No line items were returned from Jobber.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service</TableHead>
                        <TableHead className='w-16 text-right'>Qty</TableHead>
                        <TableHead className='w-28 text-right'>Unit</TableHead>
                        <TableHead className='w-28 text-right'>Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lineItems.map((item, idx) => {
                        const qtyValue = toNumber(item.quantity)
                        // Handle both camelCase and snake_case field names
                        const unitPriceValue = toNumber(item.unitPrice ?? item.unit_price ?? item.unitCost ?? item.unit_cost)
                        const lineTotal = toNumber(item.total) ?? (qtyValue != null && unitPriceValue != null ? qtyValue * unitPriceValue : undefined)
                        return (
                          <TableRow key={item.id ?? idx}>
                            <TableCell>
                              <div className='text-sm font-medium'>{item.name || `Line ${idx + 1}`}</div>
                              {item.description && (
                                <div className='text-xs text-muted-foreground'>{item.description}</div>
                              )}
                            </TableCell>
                            <TableCell className='text-right tabular-nums'>{qtyValue ?? '—'}</TableCell>
                            <TableCell className='text-right tabular-nums'>{unitPriceValue != null ? formatCurrency(unitPriceValue) : '—'}</TableCell>
                            <TableCell className='text-right tabular-nums'>{lineTotal != null ? formatCurrency(lineTotal) : '—'}</TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
              <div className='rounded-lg border p-4 space-y-4'>
                <div className='flex items-center gap-2 text-sm font-semibold'>
                  <Receipt className='h-4 w-4 text-muted-foreground' />
                  Invoice Summary
                </div>
                <div className='flex items-center justify-between text-sm'>
                  <span>Total quoted</span>
                  <span className='font-semibold'>
                    {invoiceDetails.total != null ? formatCurrency(invoiceDetails.total) : '—'}
                  </span>
                </div>
                {invoiceDetails.invoices.length > 0 ? (
                  <div className='space-y-1 text-xs text-muted-foreground'>
                    {invoiceDetails.invoices.map((invoice: any, idx: number) => (
                      <div key={invoice.id ?? idx} className='flex items-center justify-between'>
                        <span>Invoice #{invoice.invoiceNumber || invoice.id || idx + 1}</span>
                        {invoice.issuedAt && <span>{formatEnrichmentDate(invoice.issuedAt)}</span>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className='text-sm text-muted-foreground'>No invoices attached to this job yet.</p>
                )}
                {data?.notes && (
                  <div className='rounded-md bg-muted p-3 text-sm'>
                    <div className='flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
                      <FileText className='h-3.5 w-3.5' />
                      Job Notes
                    </div>
                    <p className='mt-1 whitespace-pre-wrap'>{data.notes}</p>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
